import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import DateRangeSelector, { getDateRangeByPeriod } from './DateRangeSelector';
import Table from './Tables';
import { useAgentApi } from '../Services/AgentApiProvider';
import { AssistantAuditOperationApi } from '../Services/AgentApiProviderTypes';
import { formatDateTimeLocal } from '../utils/formatDate';
import useLatestRequest from '../Hooks/useLatestRequest';
import useTablePagination from '../Hooks/useTablePagination';

function AgentAuditOperationsTab({ agentId }: { agentId: string }) {
  const navigate = useNavigate();
  const { organizationId } = useParams();

  const { getAgentAuditOperations } = useAgentApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [auditOperations, setAuditOperations] = useState<AssistantAuditOperationApi[]>([]);
  const [totalAuditOperations, setTotalAuditOperations] = useState<number>(0);

  const [operationId, setOperationId] = useState<string>('');
  const [threadId, setThreadId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const [appliedSearch, setAppliedSearch] = useState({ operationId: '', threadId: '' });

  const tableTitle = 'Audit Operations';
  const tableStorageKey = 'agent_audit_operations';
  const { initialPageSize, resetToggle, firstPage, pageForFilters } =
    useTablePagination(tableStorageKey);
  const beginRequest = useLatestRequest();

  const columns = [
    { name: 'ID', selector: (row: AssistantAuditOperationApi) => row.id, sortable: true },
    {
      name: 'Thread ID',
      selector: (row: AssistantAuditOperationApi) => row.thread_id,
      sortable: true,
    },
    {
      name: 'Duration',
      selector: (row: AssistantAuditOperationApi) => row.duration,
      sortable: true,
    },
    {
      name: 'Created',
      selector: (row: AssistantAuditOperationApi) => formatDateTimeLocal(row.created_at),
      sortable: true,
    },
  ];

  // ── Handlers ──────────────────────────────────────────────────────────
  const handlePeriodChange = useCallback(
    (period: { start_date: string; end_date: string | null }) => {
      setStartDate(period?.start_date);
      setEndDate(period?.end_date);
    },
    [],
  );

  const fetchAuditOperations = async (
    skip: number,
    limit: number,
    opId: string | null = appliedSearch.operationId || null,
    thId: string | null = appliedSearch.threadId || null,
  ): Promise<void> => {
    const isLatest = beginRequest();
    try {
      let start = startDate;
      let end = endDate;

      if (!start) {
        const range = getDateRangeByPeriod('today');
        start = range?.start_date;
        end = range?.end_date;
      }

      setLoading(true);
      const response = await getAgentAuditOperations(agentId, skip, limit, start, end, opId, thId);
      if (!isLatest()) return;
      setAuditOperations(response.items);
      setTotalAuditOperations(response.totalCount);
    } catch (err) {
      if (!isLatest()) return;
      console.error('Error fetching audit operations:', err);
      setError('Failed to load audit operations');
    } finally {
      if (isLatest()) setLoading(false);
    }
  };

  const handleSearchByOperationIdChange = (e: React.ChangeEvent<HTMLInputElement>): void =>
    setOperationId(e.target.value);
  const handleSearchByThreadIdChange = (e: React.ChangeEvent<HTMLInputElement>): void =>
    setThreadId(e.target.value);

  const handleRowClick = (row: AssistantAuditOperationApi): void => {
    navigate(`/organizations/${organizationId}/agents-hub/${agentId}/audit-operations/${row.id}`);
  };

  const applySearch = (search: { operationId: string; threadId: string }): void => {
    setAppliedSearch(search);
    const { skip, limit } = firstPage();
    fetchAuditOperations(skip, limit, search.operationId || null, search.threadId || null);
  };

  const handleSearchByOperationIdKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      setThreadId('');
      applySearch({ operationId, threadId: '' });
    }
  };

  const handleSearchByThreadIdKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      setOperationId('');
      applySearch({ operationId: '', threadId });
    }
  };

  const handleSearchByOperationIdClear = (): void => {
    setOperationId('');
    applySearch({ ...appliedSearch, operationId: '' });
  };

  const handleSearchByThreadIdClear = (): void => {
    setThreadId('');
    applySearch({ ...appliedSearch, threadId: '' });
  };

  useEffect(() => {
    if (!startDate) return;
    const { skip, limit } = pageForFilters();
    fetchAuditOperations(skip, limit);
  }, [startDate, endDate]);

  return (
    <div className="radius-yes p-2 bg-yes col-6">
      <div className="gap d-flex flex-row-to-col flex-column">
        <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
          <div className="radius-yes p-2 bg-yes col-12">
            <div className={'mb-3 p-2'}>
              <DateRangeSelector
                storageKey="agent-audit-operations"
                onChange={handlePeriodChange}
              />
              <div style={{ display: 'flex', gap: '10px' }} className="mt-2">
                <div className="search" style={{ flex: 1 }}>
                  <div className="search-wrapper">
                    <div className="icon-wrapper">
                      <i className="fa-solid fa-magnifying-glass"></i>
                    </div>
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search by ID..."
                      value={operationId}
                      onChange={handleSearchByOperationIdChange}
                      onKeyDown={handleSearchByOperationIdKeyDown}
                    />
                    {operationId && (
                      <div
                        className="icon-wrapper"
                        style={{ cursor: 'pointer' }}
                        onClick={handleSearchByOperationIdClear}
                      >
                        <i className="fa-solid fa-times"></i>
                      </div>
                    )}
                  </div>
                </div>
                <div className="search" style={{ flex: 1 }}>
                  <div className="search-wrapper">
                    <div className="icon-wrapper">
                      <i className="fa-solid fa-magnifying-glass"></i>
                    </div>
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search by Thread ID..."
                      value={threadId}
                      onChange={handleSearchByThreadIdChange}
                      onKeyDown={handleSearchByThreadIdKeyDown}
                    />
                    {threadId && (
                      <div
                        className="icon-wrapper"
                        style={{ cursor: 'pointer' }}
                        onClick={handleSearchByThreadIdClear}
                      >
                        <i className="fa-solid fa-times"></i>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {error ? (
              <div>Error: {error}</div>
            ) : (
              <Table
                title={tableTitle}
                storageKey={tableStorageKey}
                columns={columns}
                data={auditOperations}
                onRowClicked={handleRowClick}
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={initialPageSize}
                resetPaginationToggle={resetToggle}
                paginationTotalRows={totalAuditOperations}
                onChangePage={(skip: number, limit: number) => fetchAuditOperations(skip, limit)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgentAuditOperationsTab;
